import { RbcReason, UsercryptMultiselect } from '@calm/evexia-web-sdk';
import { UsercryptChoiceIcon } from '@rhg/components/usercrypt-choice-icon';
import { useActiveCircle } from '@rhg/hooks/use-active-circle';
import { useFormattedConditions } from '@rhg/hooks/use-formatted-conditions';
import { useLatestCheckinEntry } from '@rhg/hooks/use-latest-checkin-entry';
import { useScreeningResults } from '@rhg/hooks/use-screening-results';
import { CheckinResultItem } from '@rhg/hooks/use-screening-results/types';
import { ConditionsOutline, DailySpark } from '@rhg/libs/material/icons';
import { CheckinTypeKey } from '@rhg/utils/usercrypt/checkins';
import { useMemo } from 'react';
import { usePartnerDisplayLogic } from '../use-partner-display-logic';
import { useIsRegisteredAndSponsored } from '../use-sponsorship-eligibility';
import { MatchingChoicesPodData, MatchingPod } from './types';

interface LearnWhyDialogDataResponse {
    anxietyPodData: CheckinResultItem | undefined;
    depressionPodData: CheckinResultItem | undefined;
    conditionsPodData: MatchingChoicesPodData | undefined;
    topicsPodData: MatchingChoicesPodData | undefined;
    goalsPodData: MatchingChoicesPodData | undefined;
    isLoading: boolean;
}

const getMentalHealthPodData = (
    checkinResult: CheckinResultItem | undefined,
    reasonScore: number | undefined
): CheckinResultItem | undefined => {
    if (typeof reasonScore !== 'number' || !checkinResult) {
        return undefined;
    }

    return checkinResult;
};

const getMatchingChoicesPodIcon = (
    choice: string,
    pod: MatchingPod,
    choiceMetadata?: UsercryptMultiselect
) => {
    switch (pod) {
        case MatchingPod.Goals: {
            const choiceImage = choiceMetadata?.choices.find(
                (c) => c.name === choice
            )?.image;
            return <UsercryptChoiceIcon image={choiceImage} />;
        }
        case MatchingPod.Topics:
            return <DailySpark />;
        case MatchingPod.Conditions:
            return <ConditionsOutline />;
    }
};

const getMatchingChoiceDisplayNameAndCategory = (
    choice: string,
    choiceMetadata?: UsercryptMultiselect
) => {
    const match = choiceMetadata?.choices.find((c) => c.name === choice);
    return {
        displayName: match?.displayName ?? choice,
        category: match?.category,
    };
};

const getMatchingChoicesPodData = (
    matchingChoices: string[] | undefined,
    totalCount: number | undefined,
    pod: MatchingPod,
    choiceMetadata?: UsercryptMultiselect
): MatchingChoicesPodData | undefined => {
    if (!matchingChoices || typeof totalCount === 'undefined') {
        return undefined;
    }

    return {
        totalCount,
        matchingChoices: matchingChoices.map((choice) => {
            const displayNameAndCategory =
                getMatchingChoiceDisplayNameAndCategory(choice, choiceMetadata);

            return {
                label: choice,
                icon: getMatchingChoicesPodIcon(choice, pod, choiceMetadata),
                ...displayNameAndCategory,
            };
        }),
    };
};

export const useLearnWhyDialogData = (
    reason?: RbcReason
): LearnWhyDialogDataResponse => {
    const { activeCircleId } = useActiveCircle();

    const { latestResult: _latestScreeningResult } = useScreeningResults();
    const { latestEntrySummary: latestGoals } = useLatestCheckinEntry(
        CheckinTypeKey.Goal
    );
    const { latestEntrySummary: latestTopics } = useLatestCheckinEntry(
        CheckinTypeKey.Topic
    );
    const { addedConditions } = useFormattedConditions(activeCircleId);

    const { showScreeningResults, isLoading: isLoadingDisplayLogic } =
        usePartnerDisplayLogic();
    const {
        isRegistered,
        isSponsored,
        isLoading: isRegCheckLoading,
    } = useIsRegisteredAndSponsored();
    const bypassResults = isSponsored && isRegistered && !showScreeningResults;

    const latestScreeningResult =
        isRegCheckLoading || isLoadingDisplayLogic || bypassResults
            ? undefined
            : _latestScreeningResult;

    const anxietyPodData = useMemo(
        () =>
            getMentalHealthPodData(
                latestScreeningResult?.gad,
                reason?.gadScore
            ),
        [latestScreeningResult?.gad, reason?.gadScore]
    );

    const depressionPodData = useMemo(
        () =>
            getMentalHealthPodData(
                latestScreeningResult?.phq,
                reason?.phqScore
            ),
        [latestScreeningResult?.phq, reason?.phqScore]
    );

    const goalsPodData = useMemo(
        () =>
            getMatchingChoicesPodData(
                reason?.goals,
                latestGoals?.latestEntry?.checkins.values.length,
                MatchingPod.Goals,
                latestGoals?.meta.requirements?.primary?.multiselect
            ),
        [
            reason?.goals,
            latestGoals?.latestEntry?.checkins.values.length,
            latestGoals?.meta.requirements?.primary?.multiselect,
        ]
    );

    const topicsPodData = useMemo(
        () =>
            getMatchingChoicesPodData(
                reason?.topics,
                latestTopics?.latestEntry?.checkins.values.length,
                MatchingPod.Topics,
                latestTopics?.meta.requirements?.primary?.multiselect
            ),
        [
            reason?.topics,
            latestTopics?.latestEntry?.checkins.values.length,
            latestTopics?.meta.requirements?.primary?.multiselect,
        ]
    );

    const conditionsPodData = useMemo(
        () =>
            getMatchingChoicesPodData(
                reason?.conditions,
                addedConditions.length,
                MatchingPod.Conditions
            ),
        [reason?.conditions, addedConditions.length]
    );

    const isLoading = useMemo(
        () =>
            !anxietyPodData &&
            !depressionPodData &&
            !goalsPodData &&
            !topicsPodData &&
            !conditionsPodData,
        [
            anxietyPodData,
            depressionPodData,
            goalsPodData,
            topicsPodData,
            conditionsPodData,
        ]
    );

    return {
        anxietyPodData,
        depressionPodData,
        goalsPodData,
        topicsPodData,
        conditionsPodData,
        isLoading,
    };
};
